import { useContext, useMemo, useState, useEffect } from 'react';
import { useReactTable, ColumnDef, getPaginationRowModel, getCoreRowModel, FilterFn, getFilteredRowModel, flexRender, SortingState, getSortedRowModel } from '@tanstack/react-table';
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils';
import { COLUMNS, getDiffClassName } from './TransactionsColumns';
import { t } from 'i18next';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Context } from '../../../../index';
import '../table.css';
import { observer } from 'mobx-react-lite';
import NotFound from '../../../Other/NotFound';
import SpinnerLoader from '../../../Loaders/SpinnerLoader';
import Search from '../../../Other/Search';
declare module '@tanstack/react-table' {
	interface FilterFns {
		fuzzy: FilterFn<unknown>;
	}
	interface FilterMeta {
		itemRank: RankingInfo;
	}
}
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
	const itemRank = rankItem(row.getValue(columnId), value);
	addMeta({
		itemRank,
	});
	return itemRank.passed;
};

function DebouncedInput({
	value: initialValue,
	onChange,
	...props
}: {
	value: string | number;
	onChange: (value: string | number) => void;
	debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
	const [value, setValue] = useState(initialValue);

	useEffect(() => {
		setValue(initialValue);
	}, [initialValue]);

	useEffect(() => {
		onChange(value);
	}, [value]);

	return (
		<Search 
            class='searchProducts'
            placeholder={t('main.search.supposeText')}
            value={value}
            onChange={(e:string) => setValue(e)}
            {...props}
        />
	);
}

function TransactionsTable() {
	const { store } = useContext(Context);
	const { t, i18n } = useTranslation();
	const languages = i18n.language;

	async function getTransactions() {
		const transactions = await store.profileTransactions();
		if (transactions) {
			transactions.map((el: any) => {
				el.date = moment(el.date).format('DD.MM.YYYY HH:mm:ss');

				el.transaction = 
				el.method === 'deposit' ? t('profile.transactions.status.deposit', {source: t(`merchants.${el?.deposit?.source}`)}):
				el.method === 'deposit_refund' ? t('profile.transactions.status.deposit_refund', {source: t(`merchants.${el?.deposit?.source}`)}) :
				el.method === 'promocode' ? t(`profile.transactions.status.promocode.${['balance', 'partner'].includes(el?.promocode?.type) ? 'balance' : 'nullp'}`, {promocode: el?.promocode?.name}):
				['buy', 'refund', 'receive'].includes(el.method) ? `${t(`profile.transactions.status.${el.method}`, {item: (languages.includes('ru') ? el?.buy?.nameRU:el?.buy?.nameEN)+`${el?.buy?.plan?.duration ? ' '+t('defaults.time.days', {count: el?.buy?.plan?.duration}):''}`})} ${new Intl.NumberFormat('ru-RU').format(el?.buy?.qnty)} ${t('defaults.pcs')}`:
				['unwrap'].includes(el.method) ? `${t(`profile.transactions.status.${el.method}`, {item: (languages.includes('ru') ? el?.unwrap?.nameRU: el?.unwrap?.nameEN)})}`:
				['transfer_to','transfer_from'].includes(el.method) ? t(`profile.transactions.status.${el.method}`, {steamID64: el?.transfer?.steamID64}): 
				el.method === 'inGame' ? (
					el?.inGame?.statsWipePlace ? t('profile.transactions.status.inGame_stats', {statsWipePlace: el?.inGame?.statsWipePlace}): 
					el?.inGame?.newcomersPlace ? t('profile.transactions.status.inGame_newcomers', { count: el?.inGame?.newcomersPlace, ordinal: true }):
					el?.inGame?.newcomersPlace === 0 || el?.inGame?.statsWipePlace === 0 ? t('profile.transactions.status.accumulated') : ''
				):
				el.method === 'system' ? t(`profile.transactions.status.system_${el?.commentKey}`): 
				'Unknown Transaction'
			});
			store.setTransactions(transactions);
		}
	}
	useMemo(() => {
		getTransactions();
	}, [languages]);
	const columns = useMemo<ColumnDef<any>[]>(() => COLUMNS(t), [languages]);
	const data = store.transactions;
	const [sorting, setSorting] = useState<SortingState>([]);
	const [globalFilter, setGlobalFilter] = useState('');
	const tableInstance = useReactTable({
		columns,
		data,
		filterFns: {
			fuzzy: fuzzyFilter,
		},
		state: {
			globalFilter,
			sorting,
		},
		onSortingChange: setSorting,
		onGlobalFilterChange: setGlobalFilter,
		globalFilterFn: 'fuzzy',
		getFilteredRowModel: getFilteredRowModel(),
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
	});
	const { canPreviousPage, canNextPage, setPageSize }: any = tableInstance;
	useEffect(() => {
		setPageSize(10);
	}, []);
	return (
		<>
			{store.isTransactionsLoaded ? (
				<>
					{data.length ? (
						<>
							<DebouncedInput value={globalFilter ?? ''} onChange={(value) => setGlobalFilter(String(value))} />
							<table className='profile___table transactions'>
								<thead>
									{tableInstance.getHeaderGroups().map((headerGroup: any) => (
										<tr key={headerGroup.id}>
											{headerGroup.headers.map((header: any) => {
												return (
													<th className={`${header.column.getIsSorted() ? 'profile_table sorted' : 'profile_table'} ${header.id}`} title='' colSpan={header.colSpan} key={header.id} onClick={header.column.getToggleSortingHandler()}>
														<button>
															{flexRender(header.column.columnDef.header, header.getContext())}
															{{ asc: ' ⬆', desc: ' ⬇' }[header.column.getIsSorted() as string] ?? null}
														</button>
													</th>
												);
											})}
										</tr>
									))}
								</thead>
								<tbody>
									{tableInstance.getRowModel().rows.map((row: any) => {
										return (
											<tr key={row.id}>
												{row.getVisibleCells().map((cell: any) => {
													if (['amount'].includes(cell.column.id))
														return (
															<td className={getDiffClassName(cell.getValue()?.donate + cell.getValue()?.bonus)} key={cell.id}>
																{flexRender(cell.column.columnDef.cell, cell.getContext())}
															</td>
														);
													return <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>;
												})}
											</tr>
										);
									})}
								</tbody>
							</table>
							{(tableInstance.getPageCount()>0) ? <div className='table_pagination'>
								<button onClick={() => tableInstance.previousPage()} disabled={!tableInstance.getCanPreviousPage()} tabIndex={!canPreviousPage ? -1 : undefined}>
									<svg className='pagination_next-1' viewBox='-2.4 -2.4 28.8 28.8' fill='none' xmlns='http://www.w3.org/2000/svg'>
										<path d='M17.504.991 6.496 12l11.008 11.009' stroke='#200e32' strokeWidth='6.605256' strokeLinecap='round' strokeLinejoin='round' />
									</svg>
								</button>
								<div className='currentPage qnty-1'>
									{tableInstance.getState().pagination.pageIndex + 1}/{tableInstance.getPageCount()}
								</div>
								<button onClick={() => tableInstance.nextPage()} disabled={!tableInstance.getCanNextPage()} tabIndex={!canNextPage ? -1 : undefined}>
									<svg className='pagination_next-1' viewBox='-2.4 -2.4 28.8 28.8' fill='none' xmlns='http://www.w3.org/2000/svg'>
										<path d='M6.496 23.009 17.504 12 6.496.991' stroke='#200e32' strokeWidth='6.605256' strokeLinecap='round' strokeLinejoin='round' />
									</svg>
								</button>
							</div>:
							<NotFound />}
						</>
					) : (
						<div className='table_empy'>{t('profile.transactions.empty')}</div>
					)}
				</>
			) : (
				<SpinnerLoader />
			)}
		</>
	);
}

export default observer(TransactionsTable);
